import React, { useCallback, useState } from 'react';
import { useRouter } from 'expo-router';
import { StyleSheet } from 'react-native';

import { LoadingAnimation, useResponsive } from '@sg/garnish';
import { ReorderRail } from '@sg/shared-fe-features-menu';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import {
  HomeV2,
  useHomeContent,
  type HomeContent,
  getBannerContent,
  useHomeMenu,
  useHomeFiltering,
  HomeProductCategory,
} from '@order/features-home';

import { AppFooter, MenuReorderRail } from '../../../../components';
import { getLastInteractedDeliveryDetails, useLastInteractedStore } from '../../../../context';
import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { useMenuReorderRailOrders, useOneClickAddToBag } from '../../../../hooks';

export const HomeScreenContentV2 = () => {
  const isLoggedIn = useIsLoggedIn();
  const { currentBreakpoint } = useResponsive();
  const router = useRouter();

  // ─── Menu ──────────────────────────────────────────────────────────────────

  const lastInteractedStore = useLastInteractedStore() ?? {};
  const { restaurantId, restaurantSlug = '', restaurantName, addressId } = lastInteractedStore;
  const deliveryDetails = getLastInteractedDeliveryDetails(lastInteractedStore);

  const { products, isLoadingMenu } = useHomeMenu({ restaurantId, addressId });

  // ─── Content ───────────────────────────────────────────────────────────────

  const entry = useContentfulContentTypeEntry<HomeContent>({ contentType: 'homeScreen', include: 2 }); // prettier-ignore
  const content = useHomeContent(entry?.data?.fields);
  const isLoadingContent = entry?.fetching;

  // ─── Filtering ─────────────────────────────────────────────────────────────

  const { filterableCategories, genericCategories } = useHomeFiltering({
    products,
    filterableCategories: content.filterableCategories,
    genericCategories: content.genericCategories,
  });

  // ─── Chips State ───────────────────────────────────────────────────────────

  const [selectedCategory, setSelectedCategory] = useState<HomeProductCategory | null>(null);

  // ─── Reorder ───────────────────────────────────────────────────────────────

  const { mostRecentLineItems, mostRecentOrder, isFetchingMostRecentOrders } =
    useMenuReorderRailOrders({
      restaurantId: Number.isNaN(Number(restaurantId)) ? 0 : Number(restaurantId),
      enabled: isLoggedIn && Boolean(restaurantId),
    });

  // Filtering should only hide the reorder rail on XS.
  const shouldHideReorderRailOnXS = Boolean(selectedCategory) && currentBreakpoint.isXS;

  const canRenderReorderRail =
    !shouldHideReorderRailOnXS &&
    restaurantId &&
    restaurantSlug &&
    restaurantName &&
    mostRecentLineItems.length > 0;

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const { addProductToBag } = useOneClickAddToBag({
    restaurantSlug,
    deliveryDetails,
  });

  const openMenu = useCallback(() => {
    router.navigate('/menu');
  }, [router]);

  const openProductDetails = useCallback(
    (slug: string) => {
      router.navigate(`/${restaurantSlug}/${slug}`);
    },
    [restaurantSlug, router],
  );

  const handleAddProductToBag = useCallback(
    (id: string, name: string) => {
      return addProductToBag({ id, name }, 1);
    },
    [addProductToBag],
  );

  // ───────────────────────────────────────────────────────────────────────────

  if (isLoadingContent || isLoadingMenu || isFetchingMostRecentOrders) {
    return <LoadingAnimation />;
  }

  return (
    <HomeV2.Container>
      <HomeV2.Container.Content>
        <HomeV2.Banner
          size={isLoggedIn ? 'small' : 'large'}
          content={getBannerContent(content, isLoggedIn, currentBreakpoint.isXS)}
        />

        {canRenderReorderRail && !currentBreakpoint.isXS ? (
          <HomeV2.ReorderRail.Container>
            <ReorderRail.Header style={styles.reorderRail}>
              <HomeV2.ReorderRail.SideWrapper />

              <ReorderRail.Heading />

              <HomeV2.ReorderRail.SideWrapper>
                <MenuReorderRail.SeeAll
                  restaurantId={restaurantId}
                  deliveryDetails={deliveryDetails}
                />
              </HomeV2.ReorderRail.SideWrapper>
            </ReorderRail.Header>

            <MenuReorderRail.List
              style={styles.reorderRail}
              lineItems={mostRecentLineItems}
              mostRecentOrder={mostRecentOrder}
              restaurantId={restaurantId}
              restaurantName={restaurantName}
              restaurantSlug={restaurantSlug}
              deliveryDetails={deliveryDetails}
            />
          </HomeV2.ReorderRail.Container>
        ) : null}

        {filterableCategories.length > 0 ? (
          <HomeV2.FilterableCategories
            title={content.filtersTitle}
            filterableCategories={filterableCategories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
            openMenu={openMenu}
            openProductDetails={openProductDetails}
            addProductToBag={handleAddProductToBag}
          />
        ) : null}

        {canRenderReorderRail && currentBreakpoint.isXS ? (
          <HomeV2.ReorderRail.Container>
            <ReorderRail.Header style={styles.reorderRail}>
              <ReorderRail.Heading />

              <MenuReorderRail.SeeAll
                restaurantId={restaurantId}
                deliveryDetails={deliveryDetails}
              />
            </ReorderRail.Header>

            <MenuReorderRail.List
              style={styles.reorderRail}
              lineItems={mostRecentLineItems}
              mostRecentOrder={mostRecentOrder}
              restaurantId={restaurantId}
              restaurantName={restaurantName}
              restaurantSlug={restaurantSlug}
              deliveryDetails={deliveryDetails}
            />
          </HomeV2.ReorderRail.Container>
        ) : null}

        {genericCategories.length > 0 && !selectedCategory ? (
          <HomeV2.Container.AnimatedContent>
            <HomeV2.GenericCategories
              genericCategories={genericCategories}
              openMenu={openMenu}
              openProductDetails={openProductDetails}
              addProductToBag={handleAddProductToBag}
            />
          </HomeV2.Container.AnimatedContent>
        ) : null}

        <HomeV2.Footer
          title={content.footerTitle}
          imageUrl={content.footerImageUrl}
          imageA11y={content.footerImageA11y}
          onPress={openMenu}
        />
      </HomeV2.Container.Content>

      {currentBreakpoint.isXS ? null : <AppFooter />}
    </HomeV2.Container>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  reorderRail: {
    paddingHorizontal: 0, // override default padding
  },
});
